import styles from './PostsTableStatusCell.module.css';
import PostUrlPopout from 'Modules/Plans/components/PostUrlPopout/PostUrlPopout';
import { creatorbase } from '@round/api';
import cn from 'classnames';
import { CellContext } from '@tanstack/react-table';
import { getTableMetaHelper, Skeleton } from '@round/ui-kit';
import { BasePostTableMeta } from 'Modules/Plans/Posts/types';
import PostManualStatusSelect from './components/PostManualStatusSelect/PostManualStatusSelect';
import { useSetPostManualStatus } from '@round/creatorbase-helpers/api-hooks';
import { showNotification } from 'helpers';
import { useLayoutEffect, useState } from 'react';
import DraftExpectedByDatePicker from './components/DraftExpectedByDatePicker';
import PostUrlActions from 'Modules/Plans/components/PostUrlActions/PostUrlActions';

export type StatusData = {
    postUrl: string | null;
    adCode?: string;
    platform: creatorbase.Platform;
    isReadOnly?: boolean;
};

type Row = creatorbase.PublicPost;

type Context<T extends Row> = CellContext<T, StatusData>;

const getTableMeta = getTableMetaHelper<BasePostTableMeta<Row>>();

// Breathing room we want on either side of the actions. Mirrors --actions-gap in the stylesheet.
const ACTIONS_GAP = 8;

const PostsTableStatusCell = <T extends Row>({ table, row, getValue }: Context<T>) => {
    const { updatePost, managedBy, isLoading, refreshPosts, onPostStatusUpdated, onOpenPostStatsModal } =
        getTableMeta(table);
    const { postUrl, adCode, platform, isReadOnly } = getValue();
    const isManagedExternally = managedBy !== 'creatorbase';
    const status = row.original.status;
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

    // Only a live post has something worth copying or opening
    const actionablePostUrl = status === 'live' ? postUrl : null;

    const [containerElement, setContainerElement] = useState<HTMLDivElement | null>(null);
    const [statusElement, setStatusElement] = useState<HTMLDivElement | null>(null);
    const [postUrlControlsElement, setPostUrlControlsElement] = useState<HTMLDivElement | null>(null);
    const [actionsElement, setActionsElement] = useState<HTMLDivElement | null>(null);

    const canFitActionsBesideStatus = useCanFitActionsBesideStatus({
        containerElement,
        statusElement,
        postUrlControlsElement,
        actionsElement,
    });

    const {
        updateStatus,
        isLoading: isStatusUpdateLoading,
        helpers: { getErrorMessage },
    } = useSetPostManualStatus();

    if (isLoading) {
        return <Skeleton />;
    }

    return (
        <>
            <div className={styles.container} ref={setContainerElement}>
                <div
                    className={cn(styles.status, {
                        [styles.coveredByActions]: !!actionablePostUrl && !canFitActionsBesideStatus,
                    })}
                    ref={setStatusElement}
                >
                    <div className={styles.statusControl}>
                        <PostManualStatusSelect
                            isLoading={isStatusUpdateLoading}
                            isDisabled={status === 'live' || isManagedExternally || isReadOnly}
                            value={status}
                            onChange={(status) => {
                                if (status === 'draft_pending') {
                                    setIsDatePickerOpen(true);
                                    return;
                                }

                                updateStatus(
                                    {
                                        status,
                                        influencer_post_id: row.original.id,
                                    },
                                    {
                                        onSuccess: (data) => {
                                            refreshPosts?.();
                                            onPostStatusUpdated?.(data);
                                            showNotification('Status updated', 'info');
                                        },
                                        onError: (error) => {
                                            showNotification(getErrorMessage(error), 'error');
                                        },
                                    }
                                );
                            }}
                            draftExpectedBy={row.original.draft_expected_by}
                        />
                    </div>
                </div>

                <div className={styles.postUrlControls} ref={setPostUrlControlsElement}>
                    {!!actionablePostUrl && (
                        <PostUrlActions
                            ref={setActionsElement}
                            postUrl={actionablePostUrl}
                            onViewStats={onOpenPostStatsModal ? () => onOpenPostStatsModal(row.original) : undefined}
                            className={styles.postUrlActions}
                        />
                    )}

                    <PostUrlPopout
                        postUrl={postUrl}
                        adCode={adCode}
                        updatePost={updatePost ? (data) => updatePost(row.original.id, data) : undefined}
                        platform={platform}
                        status={status}
                        isReadOnly={isReadOnly || isManagedExternally}
                    />
                </div>
            </div>

            <DraftExpectedByDatePicker
                isOpen={isDatePickerOpen}
                onClose={() => {
                    setIsDatePickerOpen(false);
                }}
                isSubmitting={isStatusUpdateLoading}
                onSubmit={(date) => {
                    updateStatus(
                        {
                            status: 'draft_pending',
                            influencer_post_id: row.original.id,
                            draft_expected_by: date,
                        },
                        {
                            onSuccess: (data) => {
                                refreshPosts?.();
                                onPostStatusUpdated?.(data);
                                showNotification('Status updated', 'info');
                                setIsDatePickerOpen(false);
                            },
                            onError: (error) => {
                                showNotification(getErrorMessage(error), 'error');
                            },
                        }
                    );
                }}
            />
        </>
    );
};

export default PostsTableStatusCell;

type ActionsPlacementElements = {
    containerElement: HTMLDivElement | null;
    statusElement: HTMLDivElement | null;
    postUrlControlsElement: HTMLDivElement | null;
    actionsElement: HTMLDivElement | null;
};

/**
 * The actions always sit next to the post url button, but the column is only as wide as
 * its widest status - so in a tight cell they reach back over the status badge, which the
 * caller then hides while they're on screen.
 */
const useCanFitActionsBesideStatus = ({
    containerElement,
    statusElement,
    postUrlControlsElement,
    actionsElement,
}: ActionsPlacementElements) => {
    const [canFitActionsBesideStatus, setCanFitActionsBesideStatus] = useState(false);

    useLayoutEffect(() => {
        if (!containerElement || !statusElement || !postUrlControlsElement || !actionsElement) {
            setCanFitActionsBesideStatus(false);
            return;
        }

        const measureSlack = () => {
            const slack =
                postUrlControlsElement.getBoundingClientRect().left - statusElement.getBoundingClientRect().right;

            // The actions are positioned out of flow, so the slack doesn't shift with the
            // answer we give here - measuring can't flip-flop between the two outcomes
            setCanFitActionsBesideStatus(slack >= actionsElement.getBoundingClientRect().width + ACTIONS_GAP * 2);
        };

        measureSlack();

        // The container tracks the column's width, the status badge its own content
        const observer = new ResizeObserver(measureSlack);
        observer.observe(containerElement);
        observer.observe(statusElement);

        return () => observer.disconnect();
    }, [containerElement, statusElement, postUrlControlsElement, actionsElement]);

    return canFitActionsBesideStatus;
};
